import DOMPurify from 'dompurify';

/**
 * Sanitizes HTML content to prevent XSS attacks
 * @param html - Raw HTML string to sanitize
 * @returns Sanitized HTML string safe for rendering
 */
export function sanitizeHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'b', 'i', 'em', 'strong', 'a', 'ul', 'ol', 'li', 'p', 'br', 'span', 'div',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'code', 'pre'
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'id'],
    ALLOW_DATA_ATTR: false,
    FORBID_ATTR: ['style', 'onclick', 'onload', 'onerror'],
    FORBID_TAGS: ['script', 'object', 'embed', 'base', 'link', 'meta'],
    KEEP_CONTENT: false
  });
}

/**
 * Creates a safe object for React's dangerouslySetInnerHTML
 * @param html - Raw HTML string to sanitize
 * @returns Object with sanitized HTML for React
 */
export function createSafeHTML(html: string): { __html: string } {
  return { __html: sanitizeHtml(html) };
}

/**
 * Encrypts sensitive data for localStorage storage
 * @param data - Data to encrypt
 * @param key - Encryption key (defaults to user session key)
 * @returns Encrypted string
 */
export function encryptForStorage(data: string, key?: string): string {
  // Simple XOR encryption for localStorage (not cryptographically secure but better than plain text)
  const encryptionKey = key || 'default-storage-key';
  let encrypted = '';
  
  for (let i = 0; i < data.length; i++) {
    encrypted += String.fromCharCode(
      data.charCodeAt(i) ^ encryptionKey.charCodeAt(i % encryptionKey.length)
    );
  }
  
  return btoa(encrypted);
}

/**
 * Decrypts data from localStorage
 * @param encryptedData - Encrypted data string
 * @param key - Decryption key (defaults to user session key)
 * @returns Decrypted string
 */
export function decryptFromStorage(encryptedData: string, key?: string): string {
  try {
    // Validar que los datos no estén vacíos o corruptos
    if (!encryptedData || typeof encryptedData !== 'string') {
      console.warn('Invalid encrypted data provided to decryptFromStorage');
      return '';
    }

    // Validar que sea base64 válido antes de intentar decodificar
    const base64Pattern = /^[A-Za-z0-9+/]*={0,2}$/;
    if (!base64Pattern.test(encryptedData)) {
      console.warn('Invalid base64 format in encrypted data');
      return '';
    }

    const encryptionKey = key || 'default-storage-key';
    const encrypted = atob(encryptedData);
    let decrypted = '';
    
    for (let i = 0; i < encrypted.length; i++) {
      decrypted += String.fromCharCode(
        encrypted.charCodeAt(i) ^ encryptionKey.charCodeAt(i % encryptionKey.length)
      );
    }
    
    return decrypted;
  } catch (error) {
    console.error('Error decrypting storage data:', error);
    return '';
  }
}

/**
 * Secure localStorage wrapper with encryption
 */
export const secureStorage = {
  setItem: (key: string, value: string, encrypt: boolean = false): void => {
    try {
      const finalValue = encrypt ? encryptForStorage(value) : value;
      localStorage.setItem(key, finalValue);
    } catch (error) {
      console.error('Error setting secure storage item:', error);
    }
  },

  getItem: (key: string, encrypted: boolean = false): string | null => {
    try {
      const value = localStorage.getItem(key);
      if (!value) return null;
      
      if (encrypted) {
        const decrypted = decryptFromStorage(value);
        // Si el descifrado falla (retorna cadena vacía), limpiar la clave corrupta
        if (decrypted === '' && value !== '') {
          console.warn(`Corrupted encrypted data found for key: ${key}, removing...`);
          localStorage.removeItem(key);
          return null;
        }
        return decrypted;
      }
      
      return value;
    } catch (error) {
      console.error('Error getting secure storage item:', error);
      // Limpiar datos corruptos en caso de error
      try {
        localStorage.removeItem(key);
      } catch (cleanupError) {
        console.error('Error cleaning up corrupted storage item:', cleanupError);
      }
      return null;
    }
  },

  removeItem: (key: string): void => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Error removing secure storage item:', error);
    }
  },

  clear: (): void => {
    try {
      localStorage.clear();
    } catch (error) {
      console.error('Error clearing secure storage:', error);
    }
  },

  // Clear sensitive data on logout
  clearSensitiveData: (): void => {
    try {
      console.log('🧹 Iniciando limpieza de datos sensibles...');
      
      const sensitiveKeys = [
        'selectedCompany',
        'selectedCompanyData',
        'selectedCompanyId',
        'lastCompanyId',
        'userCompanies', 
        'userProfile',
        'authSession',
        'lastActivity'
      ];
      
      // Primero, limpiar claves específicas conocidas
      sensitiveKeys.forEach(key => {
        try {
          localStorage.removeItem(key);
        } catch (error) {
          console.error(`Error removing sensitive key ${key}:`, error);
        }
      });
      
      // Luego, buscar y limpiar cualquier clave que contenga patrones de empresa o usuario
      const allKeys = Object.keys(localStorage);
      const companyPatterns = [
        /company/i,
        /Company/,
        /lastCompany/,
        /selected/i,
        /user.*company/i,
        /empresa/i
      ];
      
      allKeys.forEach(key => {
        const shouldRemove = companyPatterns.some(pattern => pattern.test(key));
        if (shouldRemove) {
          try {
            localStorage.removeItem(key);
            console.log(`🧹 Removed pattern-matched key: ${key}`);
          } catch (error) {
            console.error(`Error removing pattern-matched key ${key}:`, error);
          }
        }
      });
      
      console.log('✅ Limpieza de datos sensibles completada');
    } catch (error) {
      console.error('Error in clearSensitiveData:', error);
      // Fallback extremo
      try {
        console.log('🚨 Aplicando limpieza fallback total...');
        localStorage.clear();
      } catch (clearError) {
        console.error('Error en limpieza fallback:', clearError);
      }
    }
  },

  // Nueva función para detectar y reparar inconsistencias
  validateAndRepairStorage: (): void => {
    try {
      console.log('🔍 Validando integridad de localStorage...');
      
      const allKeys = Object.keys(localStorage);
      let repairedCount = 0;
      let removedCount = 0;
      
      allKeys.forEach(key => {
        try {
          const value = localStorage.getItem(key);
          if (!value) return;
          
          // Verificar si parece ser datos encriptados (base64)
          if (value.length > 10 && /^[A-Za-z0-9+/=]+$/.test(value)) {
            // Intentar validar el formato base64
            try {
              atob(value);
            } catch (base64Error) {
              console.info(`🧹 Limpiando datos corruptos en ${key}`);
              localStorage.removeItem(key);
              removedCount++;
            }
          }
          
          // Verificar datos JSON corruptos
          if (value.startsWith('{') || value.startsWith('[')) {
            try {
              JSON.parse(value);
            } catch (jsonError) {
              console.info(`🧹 Limpiando JSON corrupto en ${key}`);
              localStorage.removeItem(key);
              removedCount++;
            }
          }
        } catch (error) {
          console.error(`Error validating key ${key}:`, error);
          localStorage.removeItem(key);
          removedCount++;
        }
      });
      
      if (removedCount > 0) {
        console.info(`🧹 Limpieza de localStorage completada (${removedCount} elementos corruptos removidos)`);
      } else {
        console.log('✅ localStorage validado correctamente');
      }
    } catch (error) {
      console.error('Error validating storage:', error);
    }
  }
};

/**
 * Input validation utilities
 */
export const inputValidation = {
  /**
   * Sanitizes user input to prevent injection attacks
   */
  sanitizeInput: (input: string): string => {
    return input
      .replace(/[<>]/g, '') // Remove HTML brackets
      .replace(/javascript:/gi, '') // Remove javascript protocols
      .replace(/on\w+=/gi, '') // Remove event handlers
      .trim();
  },

  /**
   * Validates email format
   */
  isValidEmail: (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  },

  /**
   * Validates phone number format
   */
  isValidPhone: (phone: string): boolean => {
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    return phoneRegex.test(phone.replace(/[\s\-\(\)]/g, ''));
  },

  /**
   * Checks for potential SQL injection patterns
   */
  containsSQLInjection: (input: string): boolean => {
    const sqlPatterns = [
      /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC|UNION|SCRIPT)\b)/gi,
      /(\-\-)|(\;)|(\|)|(\*)/g,
      /(\')|(\")|(\/\*)|(\*\/)/g
    ];
    
    return sqlPatterns.some(pattern => pattern.test(input));
  }
};